import { ReactElement, useEffect, useRef, useState } from 'react';
import { Button, Flex, Text } from '@backstage/ui';
import {
  ProxiedSignInPage,
  SignInProviderConfig,
  UserIdentity,
} from '@backstage/core-components';
import {
  ApiHolder,
  SignInPageProps,
  configApiRef,
  useApi,
  useApiHolder,
} from '@backstage/core-plugin-api';
import { useSearchParams } from 'react-router-dom';
import { LogoMark } from '../nav/LogoMark';
import styles from './signIn.module.css';

/**
 * A sign-in provider, extended with an optional brand icon for its button.
 *
 * @remarks
 * Structurally the same as the config the stock `SignInPage` takes, so
 * providers can be declared once and moved between the two.
 */
export type SignInProvider = SignInProviderConfig & { icon?: ReactElement };

/** Guest is rendered as a de-emphasized link rather than a full button. */
export type SignInProviders = ('guest' | SignInProvider)[];

export type SignInLayoutProps = SignInPageProps & {
  providers: SignInProviders;
};

/** Signs in against a single provider, returning false if there's no session. */
const signInWith = async (
  apis: ApiHolder,
  provider: SignInProvider,
  instantPopup: boolean,
  onSignInSuccess: SignInPageProps['onSignInSuccess'],
) => {
  const authApi = apis.get(provider.apiRef);
  if (!authApi) {
    throw new Error(`No API registered for provider "${provider.title}"`);
  }

  const identityResponse = await authApi.getBackstageIdentity(
    instantPopup ? { instantPopup: true } : { optional: true },
  );
  if (!identityResponse) {
    if (!instantPopup) {
      return false;
    }
    throw new Error(
      `The ${provider.title} provider is not configured to support sign-in`,
    );
  }

  const profile = await authApi.getProfile();
  onSignInSuccess(
    UserIdentity.create({
      identity: identityResponse.identity,
      authApi,
      profile,
    }),
  );
  return true;
};

export const SignInLayout = (props: SignInLayoutProps) => {
  const { onSignInSuccess, providers } = props;
  const configApi = useApi(configApiRef);
  const apis = useApiHolder();
  const [searchParams] = useSearchParams();
  const errorParam = searchParams.get('error');

  const [error, setError] = useState<Error | undefined>(
    errorParam ? new Error(errorParam) : undefined,
  );
  const [busyProvider, setBusyProvider] = useState<string>();
  const [guestPending, setGuestPending] = useState(false);
  // Until the existing-session check resolves we render nothing, so returning
  // users don't see the form flash before being let straight in.
  const [checkingSession, setCheckingSession] = useState(true);
  const sessionChecked = useRef(false);

  const title =
    configApi.getOptionalString('app.title') ?? 'Developing Software';

  const authProviders = providers.filter(
    (provider): provider is SignInProvider => provider !== 'guest',
  );
  const guestAllowed = providers.includes('guest');

  useEffect(() => {
    if (sessionChecked.current) {
      return;
    }
    sessionChecked.current = true;

    // An error bounced back from the auth backend means the existing session is
    // not usable, so go straight to the form rather than retrying it.
    if (errorParam) {
      setCheckingSession(false);
      return;
    }

    // First provider with a live session wins; the rest fall through to the form.
    (async () => {
      for (const provider of authProviders) {
        try {
          if (await signInWith(apis, provider, false, onSignInSuccess)) {
            return;
          }
        } catch {
          // Ignore and try the next provider.
        }
      }
      setCheckingSession(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSignIn = (provider: SignInProvider) => {
    setBusyProvider(provider.id);
    setError(undefined);
    signInWith(apis, provider, true, onSignInSuccess).catch(err => {
      // The user closing the popup is a deliberate cancel, not an error.
      if (err?.name !== 'PopupRejectedError') {
        setError(err);
      }
      setBusyProvider(undefined);
    });
  };

  if (checkingSession) {
    return null;
  }

  // A single provider's `message` reads "Sign in using GitHub", which only
  // restates the button beneath it, so the heading stands alone. With more than
  // one there is an actual choice to introduce.
  const subheading =
    authProviders.length > 1
      ? 'Choose how you would like to continue.'
      : undefined;

  return (
    <div className={styles.root}>
      <div className={styles.brand}>
        <div className={styles.glow} />
        <div className={styles.brandContent}>
          <Flex align="center" gap="2" mb="7">
            <LogoMark size={32} />
            <Text as="span" variant="title-small" weight="bold">
              {title}
            </Text>
          </Flex>
          <Text
            variant="body-large"
            color="secondary"
            className={styles.tagline}
          >
            Internal developer portal — services, docs and scaffolding in one
            place.
          </Text>
        </div>
        <Text
          variant="body-x-small"
          color="secondary"
          className={styles.brandFooter}
        >
          Powered by Backstage
        </Text>
      </div>

      <div className={styles.form}>
        <div className={styles.formInner}>
          <div className={styles.formHeader}>
            <Text as="h1" variant="title-medium" weight="bold">
              Sign in
            </Text>
            {subheading && (
              <Text
                variant="body-medium"
                color="secondary"
                className={styles.subheading}
              >
                {subheading}
              </Text>
            )}
          </div>

          {guestPending ? (
            <ProxiedSignInPage
              {...props}
              provider="guest"
              ErrorComponent={({ error: guestError }) => (
                <Text as="p" variant="body-small" className={styles.error}>
                  {guestError?.message ?? 'Guest sign-in failed.'}
                </Text>
              )}
            />
          ) : (
            <>
              <Flex direction="column" gap="3">
                {authProviders.map(provider => (
                  <Button
                    key={provider.id}
                    variant="primary"
                    size="medium"
                    isPending={busyProvider === provider.id}
                    iconStart={provider.icon}
                    onClick={() => handleSignIn(provider)}
                  >
                    Sign in with {provider.title}
                  </Button>
                ))}
              </Flex>

              {error && (
                <Text as="p" variant="body-small" className={styles.error}>
                  {error.message}
                </Text>
              )}

              {guestAllowed && (
                <div className={styles.guest}>
                  <Button
                    variant="tertiary"
                    size="small"
                    onClick={() => setGuestPending(true)}
                  >
                    Continue as guest
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
